import { logError } from 'utils/logger';
import { useMutation } from '@apollo/client';
import { useNotifications } from 'modules/notification';
import { getOperationName } from '@apollo/client/utilities/graphql/getFromAST';
import { CreateBPFTemplateSchema, UpdateBPFTemplateSchema, UpdateTemplateStageFieldsSchema } from '../model/schemas';
import {
  CreateBPFTemplateInput,
  CreateTemplateResponse,
  CreateTemplateVariables,
  stagesApi,
  UpdateBPFTemplateInput,
  UpdateBPFVariables,
  UpdateTemplateStageFields,
  UpdateTemplateStageFieldsVariables,
  VOTemplatesAndStagesResponse,
} from '../api';

export function useBPFMutations() {
  const { showNotification } = useNotifications();

  const [onUpdateBusinessProcessFlows, { loading: isBPFsUpdating }] = useMutation<
    VOTemplatesAndStagesResponse,
    UpdateBPFVariables
  >(stagesApi.updateVOBusinessProcessFlows, {
    refetchQueries: [getOperationName(stagesApi.fetchVOTemplatesAndStages)!],
  });

  const [onCreateTemplate, { loading: isTemplateCreating }] = useMutation<
    CreateTemplateResponse,
    CreateTemplateVariables
  >(stagesApi.createTemplate);

  const [onUpdateTemplateStageFields, { loading: isStagesUpdating }] = useMutation<
    CreateTemplateResponse,
    UpdateTemplateStageFieldsVariables
  >(stagesApi.updateTemplateStageFields, {
    refetchQueries: [getOperationName(stagesApi.fetchVOTemplatesAndStages)!],
    awaitRefetchQueries: true,
  });

  const [onUpdateTemplate, { loading: isTemplateUpdating }] = useMutation<
    CreateTemplateResponse,
    CreateTemplateVariables
  >(stagesApi.updateTemplate);

  const handleCreateTemplate = async (dto: CreateBPFTemplateInput) => {
    try {
      const { data, success, error } = CreateBPFTemplateSchema.safeParse(dto);

      if (!success) {
        logError('Zod parse error', error);
        return;
      }

      await onCreateTemplate({ variables: { input: data } });
      showNotification('Template created successfully.');
    } catch (error) {
      showNotification('Something went wrong', { type: 'error' });
      logError(error);
    }
  };

  const handleUpdateTemplate = async (dto: UpdateBPFTemplateInput) => {
    try {
      const { data, success, error } = UpdateBPFTemplateSchema.safeParse(dto);

      if (!success) {
        logError('Zod parse error', error);
        return;
      }

      await onUpdateTemplate({ variables: { input: data } });
      showNotification('Template updated successfully.');
    } catch (error) {
      showNotification('Something went wrong', { type: 'error' });
      logError(error);
    }
  };

  const handleUpdateStageAttributes = async (dto: UpdateTemplateStageFields) => {
    try {
      const { data, success, error } = UpdateTemplateStageFieldsSchema.safeParse(dto);

      if (!success) {
        logError('Zod parse error', error);
        return;
      }

      await onUpdateTemplateStageFields({ variables: { input: data } });
      showNotification('Stage Attribute updated successfully.');
    } catch (error) {
      showNotification('Something went wrong', { type: 'error' });
      logError(error);
    }
  };

  return {
    isBPFsUpdating,
    isStagesUpdating,
    isTemplateUpdating,
    isTemplateCreating,
    handleCreateTemplate,
    handleUpdateTemplate,
    onUpdateBusinessProcessFlows,
    handleUpdateStageAttributes,
  };
}
